import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MenuIcon, XIcon, MapPinIcon, PhoneIcon, MailIcon, FacebookIcon, TwitterIcon, InstagramIcon, GlobeIcon, ChevronDownIcon, TagIcon, GiftIcon, SparklesIcon } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/7XxvZjma91KZzYXXSDSoRf/Visiual_Eyes_logo.png';
const LANGUAGES = [{
  code: 'en',
  label: 'English',
  flag: '🇺🇸',
  googleCode: 'en'
}, {
  code: 'es',
  label: 'Español',
  flag: '🇪🇸',
  googleCode: 'es'
}, {
  code: 'fr',
  label: 'Français',
  flag: '🇫🇷',
  googleCode: 'fr'
}];
function triggerGoogleTranslate(langCode: string) {
  const hostname = window.location.hostname;
  if (langCode === 'en') {
    // Clear all googtrans cookies to restore English
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname}`;
    document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname}`;
    // Also try to use the Google Translate iframe to restore
    const frame = document.querySelector('.goog-te-menu-frame') as HTMLIFrameElement;
    if (frame) {
      try {
        const innerDoc = frame.contentDocument || frame.contentWindow?.document;
        const restoreItem = innerDoc?.querySelector('.goog-te-menu2-item:first-child') as HTMLElement;
        restoreItem?.click();
        return;
      } catch (e) {
        // Cross-origin, fall through to reload
      }
    }
    // Try the select combo
    const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
    if (select) {
      select.value = 'en';
      select.dispatchEvent(new Event('change'));
      // Give it a moment then reload to fully clear
      setTimeout(() => window.location.reload(), 100);
      return;
    }
    window.location.reload();
    return;
  }
  // Set translation cookies (both with and without dot prefix for domain)
  document.cookie = `googtrans=/en/${langCode}; path=/`;
  document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${hostname}`;
  document.cookie = `googtrans=/en/${langCode}; path=/; domain=${hostname}`;
  // Try using the existing Google Translate select element
  const select = document.querySelector('.goog-te-combo') as HTMLSelectElement;
  if (select) {
    select.value = langCode;
    select.dispatchEvent(new Event('change'));
  } else {
    // If widget isn't ready yet, reload to pick up the cookie
    window.location.reload();
  }
}
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Load Google Translate script once
  useEffect(() => {
    ;
    (window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement({
        pageLanguage: 'en',
        includedLanguages: 'en,es,fr',
        autoDisplay: false
      }, 'google_translate_element');
    };
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }
    // Detect current language from cookie on mount
    const match = document.cookie.match(/googtrans=\/en\/(\w+)/);
    if (match?.[1]) setCurrentLang(match[1]);
  }, []);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const navLinks = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'Team',
    href: '/team'
  }, {
    name: 'About Us',
    href: '/about'
  }, {
    name: 'Services',
    href: '/services'
  }, {
    name: 'Locations',
    href: '/locations'
  }, {
    name: 'Blog',
    href: '/blog'
  }, {
    name: 'Contact Us',
    href: '/contact'
  }, {
    name: 'Pay Bill',
    href: '/payments'
  }];
  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };
  return <>
      {/* Top Info Bar */}
      <div className="bg-brand-dark text-white text-xs py-2 hidden md:block" data-id="element-295">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center" data-id="element-296">
          <div className="flex items-center gap-6" data-id="element-297">
            <a href="https://maps.google.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-298">
              <MapPinIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-299" />
              10240 Lake Arbor Way, Mitchellville, MD 20721
            </a>
          </div>
          <div className="flex items-center gap-6" data-id="element-300">
            <a href="tel:3013249500" className="flex items-center gap-2 hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-301">
              <PhoneIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-302" />
              (301) 324-9500
            </a>
            <a href="mailto:info@visualeyesgroup.com" className="flex items-center gap-2 hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-303">
              <MailIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-304" />
              info@visualeyesgroup.com
            </a>
            <div className="flex items-center gap-3 border-l border-white/20 pl-6 ml-2" data-id="element-305">
              <a href="https://www.instagram.com/visualeyesgroup/" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-306">
                <InstagramIcon className="w-4 h-4" data-id="element-307" />
              </a>
              <a href="https://x.com/visualeyes4u" target="_blank" rel="noreferrer" aria-label="X" className="hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-308">
                <TwitterIcon className="w-4 h-4" data-id="element-309" />
              </a>
              <a href="https://www.tiktok.com/@visualeyesgroup" target="_blank" rel="noreferrer" aria-label="TikTok" className="hover:text-brand-gold transition-colors focus-ring rounded-sm" data-id="element-310">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-311">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52V6.84a4.84 4.84 0 0 1-1-.15z" data-id="element-312" />
                </svg>
              </a>
            </div>

            {/* Language Toggle */}
            <div ref={langRef} className="relative border-l border-white/20 pl-5 ml-2" data-id="element-313">
              <button onClick={() => setIsLangOpen(!isLangOpen)} className="flex items-center gap-1.5 hover:text-brand-gold transition-colors focus-ring rounded-sm py-0.5" aria-label="Select language" aria-expanded={isLangOpen} data-id="element-314">
                <GlobeIcon className="w-3.5 h-3.5 text-brand-gold" data-id="element-315" />
                <span data-id="element-316">
                  {LANGUAGES.find(l => l.code === currentLang)?.flag}
                </span>
                <span className="uppercase font-semibold" data-id="element-317">{currentLang}</span>
                <ChevronDownIcon className={`w-3 h-3 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`} data-id="element-318" />
              </button>

              {isLangOpen && <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden min-w-[140px] z-[200]" data-id="element-319">
                  {LANGUAGES.map(lang => <button key={lang.code} onClick={() => {
                setCurrentLang(lang.code);
                setIsLangOpen(false);
                triggerGoogleTranslate(lang.googleCode);
              }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${currentLang === lang.code ? 'bg-brand-gold/10 text-brand-gold font-semibold' : 'text-brand-dark hover:bg-gray-50'}`} data-id="element-320">
                      <span className="text-base" data-id="element-321">{lang.flag}</span>
                      <span data-id="element-322">{lang.label}</span>
                    </button>)}
                </div>}
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Google Translate element */}
      <div id="google_translate_element" className="hidden" data-id="element-323" />

      {/* Main Navigation */}
      <header className={`${isScrolled ? 'fixed top-0 left-0 right-0 z-50 bg-white shadow-header py-2' : 'absolute top-0 md:top-[36px] left-0 right-0 z-50 bg-transparent py-3 md:py-4'} transition-all duration-300`} data-id="element-324">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-325">
          <div className="flex justify-between items-center" data-id="element-326">
            {/* Logo */}
            <Link to="/" className="flex items-center focus-ring rounded-sm" aria-label="VisualEyes Home" data-id="element-327">
              <img src={LOGO_URL} alt="VisualEyes - A member of Vision Source" className={`h-14 md:h-[90px] w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-328" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main Navigation" data-id="element-329">
              <ul className="flex items-center gap-6" data-id="element-330">
                {navLinks.map(link => {
                const isActive = location.pathname === link.href;
                return <li key={link.name} data-id="element-331">
                      <Link to={link.href} className={`text-[15px] font-semibold transition-colors focus-ring rounded-sm px-2 py-1 ${isActive ? 'text-brand-gold' : isScrolled ? 'text-brand-dark hover:text-brand-gold' : 'text-white hover:text-brand-gold'}`} data-id="element-332">
                        {link.name}
                      </Link>
                    </li>;
              })}
              </ul>
              <a href="https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0" target="_blank" rel="noreferrer" className={`px-6 py-2.5 rounded-button font-heading font-semibold text-sm transition-colors shadow-sm focus-ring ${isScrolled ? 'bg-brand-dark text-white hover:bg-brand-dark/90' : 'bg-brand-gold text-white hover:bg-brand-gold-light'}`} data-id="element-333">
                Book Appointment
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button className={`lg:hidden p-2 rounded-md focus-ring ${isScrolled ? 'text-brand-dark' : 'text-white'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-expanded={isMobileMenuOpen} aria-label="Toggle navigation menu" data-id="element-334">
              {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-335" /> : <MenuIcon className="w-6 h-6" data-id="element-336" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-card max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-337">
            <nav className="flex flex-col px-4 pt-4 pb-6 gap-4" aria-label="Mobile Navigation" data-id="element-338">
              {/* Special Offers Banner */}
              <div className="bg-brand-gold/5 border border-brand-gold/20 rounded-lg p-4 mb-2" data-id="element-339">
                <div className="flex items-center gap-2 mb-3" data-id="element-340">
                  <TagIcon className="w-4 h-4 text-brand-gold" data-id="element-341" />
                  <h3 className="font-heading font-bold text-sm text-brand-dark uppercase tracking-wider" data-id="element-342">
                    Special Offers
                  </h3>
                </div>
                <div className="space-y-2.5 mb-4" data-id="element-343">
                  <div className="flex items-start gap-2" data-id="element-344">
                    <GiftIcon className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-345" />
                    <p className="text-sm text-neutral-text leading-tight" data-id="element-346">
                      <span className="font-semibold text-brand-dark" data-id="element-347">
                        1 Free Frame
                      </span>{' '}
                      with purchase of $99+ (Self-Pay)
                    </p>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-348">
                    <SparklesIcon className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-349" />
                    <p className="text-sm text-neutral-text leading-tight" data-id="element-350">
                      <span className="font-semibold text-brand-dark" data-id="element-351">
                        Free Contact Lens
                      </span>{' '}
                      Follow-Up
                    </p>
                  </div>
                </div>
                <a href="https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0" target="_blank" rel="noreferrer" onClick={handleNavClick} className="block w-full bg-brand-gold/10 hover:bg-brand-gold/20 text-brand-gold text-center py-2 rounded-md font-semibold text-sm transition-colors border border-brand-gold/30" data-id="element-352">
                  Get Offer
                </a>
              </div>

              <ul className="flex flex-col gap-1" data-id="element-353">
                {navLinks.map(link => {
              const isActive = location.pathname === link.href;
              return <li key={link.name} data-id="element-354">
                      <Link to={link.href} onClick={handleNavClick} className={`block px-4 py-3 font-semibold rounded-md transition-colors ${isActive ? 'bg-brand-gold/10 text-brand-gold' : 'text-brand-dark hover:bg-surface-muted hover:text-brand-gold'}`} data-id="element-355">
                        {link.name}
                      </Link>
                    </li>;
            })}
              </ul>
              <a href="https://appointments.4patientcare.app/?CoverKey=5118&Source=website%20WebSchedV4&ReferredBy=Unknon&Gcount=0" target="_blank" rel="noreferrer" onClick={handleNavClick} className="bg-brand-gold text-white text-center px-6 py-3 rounded-button font-heading font-semibold mt-2 shadow-sm" data-id="element-356">
                Book Appointment
              </a>
            </nav>
          </div>}
      </header>
    </>;
}